/**
 * Onboarding parent : « Inscrire mon / mes enfants ». Trois chemins, répétables
 * pour plusieurs enfants :
 *  - créer le compte d'un enfant sans email (compte géré, migration 0301) ;
 *  - relier le compte qu'il a déjà (code / QR de liaison, migration 0150) ;
 *  - plus tard (depuis « Mes enfants »).
 * La liste des enfants déjà reliés se met à jour au fil de l'eau.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { CheckCircle2, Link2, UserPlus } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { AvatarCircle } from '@/components/avatar-circle';
import { CreateChildForm } from './create-child-form';
import { ParentLinkCard } from '@/components/contacts/parent-link-card';
import { OnboardingFooter } from '@/features/onboarding/onboarding-ui';

const db = supabase as unknown as SupabaseClient;

type Mode = 'create' | 'link' | null;

interface LinkedChild {
  child_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

export function ParentChildrenOnboarding({
  parentId,
  onFinish,
  finishing,
}: {
  parentId: string;
  onFinish: () => void;
  finishing: boolean;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>(null);
  // Même clé que `useChildrenAccess` : rafraîchie toutes les 4 s pendant
  // l'onboarding pour voir arriver un enfant relié par code / QR.
  const children = useQuery({
    queryKey: ['children-access', parentId],
    enabled: !!parentId,
    refetchInterval: 4000,
    queryFn: async (): Promise<LinkedChild[]> => {
      const { data, error } = await db.rpc('get_children_access');
      if (error) throw new Error(error.message);
      return (data ?? []) as LinkedChild[];
    },
  });
  const list = children.data ?? [];

  const choice = (
    value: Exclude<Mode, null>,
    title: string,
    hint: string,
    Icon: typeof UserPlus,
  ) => (
    <button
      type="button"
      aria-pressed={mode === value}
      onClick={() => setMode(mode === value ? null : value)}
      className={cn(
        'flex min-h-[2.75rem] w-full items-start gap-3 rounded-2xl border-2 p-4 text-left active:scale-[0.98]',
        mode === value
          ? 'border-primary-500 bg-primary-50 ocean:bg-primary-500/15'
          : 'border-ink-100 ocean:bg-white/5 bg-white',
      )}
    >
      <Icon className="text-primary-600 mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <span>
        <span className="text-ink-900 block font-semibold">{title}</span>
        <span className="text-ink-500 block text-sm">{hint}</span>
      </span>
    </button>
  );

  return (
    <div className="space-y-4">
      {list.length > 0 && (
        <div className="space-y-2 rounded-2xl bg-green-50 p-3" role="status">
          <p className="flex items-center gap-2 text-sm font-semibold text-green-800">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            {list.length === 1 ? 'Enfant inscrit' : `${list.length} enfants inscrits`}
          </p>
          <ul className="flex flex-wrap gap-2">
            {list.map((c) => (
              <li
                key={c.child_id}
                className="flex items-center gap-1.5 rounded-full bg-white py-1 pl-1 pr-3 text-sm"
              >
                <AvatarCircle name={c.display_name ?? '?'} src={c.avatar_url} size="xs" />
                {(c.display_name ?? 'Enfant').split(' ')[0]}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2">
        {choice(
          'create',
          list.length > 0 ? 'Inscrire un autre enfant' : 'Inscrire mon enfant',
          'Je crée son compte, sans email : il l’utilise sur mon téléphone ou le sien.',
          UserPlus,
        )}
        {mode === 'create' && (
          <CreateChildForm
            parentId={parentId}
            onCreated={() => {
              setMode(null);
              void qc.invalidateQueries({ queryKey: ['children-access', parentId] });
            }}
          />
        )}
        {choice(
          'link',
          'Mon enfant a déjà un compte',
          'On relie vos comptes avec un code ou un QR code.',
          Link2,
        )}
        {mode === 'link' && <ParentLinkCard userId={parentId} audience="parent" />}
      </div>

      <OnboardingFooter
        hint={
          list.length > 0 ? undefined : 'Tu pourras inscrire tes enfants depuis « Mes enfants ».'
        }
      >
        <Button
          type="button"
          size="lg"
          fullWidth
          variant={list.length > 0 ? 'primary' : 'secondary'}
          loading={finishing}
          onClick={onFinish}
        >
          {list.length > 0 ? 'Aller à mon tableau de bord' : 'Plus tard'}
        </Button>
      </OnboardingFooter>
    </div>
  );
}
